import { useCallback, useMemo } from 'react'

import {
    createOptions,
    extractPlayersFromEvents,
    getMatchEditorEventTypeLabel,
    makePrettierLabel,
    Option,
} from '@components/MatchEventEditor/utils'

import {
    MatchEventEditorCardLocation,
    MatchEventEditorCardReason,
    MatchEventEditorEventType,
    MatchEventEditorFixtureExtraInfo,
    MatchEventEditorGoalExtraInfo,
    MatchEventEditorPenaltyMissedReason,
    MatchEventEditorSubstitutionReason,
    MatchEventEditorVarCheckExtraInfo,
    MatchEventEditorVarOverExtraInfo,
    MatchEventPeriodNumber,
} from '@common/types'

import { useMatchEditorFormContext } from '@contexts/MatchEditorFormContext'

import { useMatchEventEditorData } from '@queries/useMatchEventEditorData'

export type EventFormOptions = {
    teams: Option[]
    players: {
        [x: number]: Option[]
    }
    types: Option[]
    periods: Option[]
    extraInfo: Option[]
    cardLocation: Option[]
    cardReason: Option[]
    penaltyMissedReason: Option[]
    substitutionReason: Option[]
    varOverReview: Option[]
    matchEvents: Option[]
}

export const useEventFormOptions = (): EventFormOptions => {
    const { data, events, lineups } = useMatchEventEditorData()
    const { zodForm, fakePlayers } = useMatchEditorFormContext()

    const selectedEventType = zodForm.watch('type.value')

    const eventFakePlayers = useMemo(() => {
        if (!data || !events) {
            return {}
        }

        return {
            [data?.team1_info?.id]: extractPlayersFromEvents(data?.team1_info?.id, events),
            [data?.team2_info?.id]: extractPlayersFromEvents(data?.team2_info?.id, events),
        }
    }, [data, events])

    const teams = useMemo<Option[]>(() => {
        if (!data) {
            return []
        }

        return [
            {
                value: data?.team1_info?.id,
                label: data?.team1_info?.name,
            },
            {
                value: data?.team2_info?.id,
                label: data?.team2_info?.name,
            },
        ]
    }, [data])

    const getPlayers = useCallback(
        (teamId: string) => {
            const teamLineup = lineups[teamId] ?? []
            const existingFakePlayers = eventFakePlayers?.[teamId]?.length ? [...eventFakePlayers[teamId]] : []

            if (teamLineup.length) {
                const realPlayers = teamLineup?.map(({ id, name }) => ({ value: String(id), label: name })) ?? []

                return [...fakePlayers, ...existingFakePlayers, ...realPlayers]
            }

            return [...fakePlayers, ...existingFakePlayers]
        },
        [eventFakePlayers, fakePlayers, lineups],
    )

    const team1Players = useMemo<Option[]>(() => {
        if (!data || !lineups) {
            return []
        }

        return getPlayers(data.team1_info?.id)
    }, [data, lineups, getPlayers])

    const team2Players = useMemo<Option[]>(() => {
        if (!data || !lineups) {
            return []
        }

        return getPlayers(data.team2_info?.id)
    }, [data, lineups, getPlayers])

    const players = useMemo(
        () => ({
            [data.team1_info?.id]: team1Players,
            [data.team2_info?.id]: team2Players,
        }),
        [data.team1_info?.id, data.team2_info?.id, team1Players, team2Players],
    )

    const extraInfo = useMemo<Option[]>(() => {
        switch (Number(selectedEventType)) {
            case MatchEventEditorEventType.goal:
                return createOptions(MatchEventEditorGoalExtraInfo, makePrettierLabel)

            case MatchEventEditorEventType.video_assistant_referee:
                return createOptions(MatchEventEditorVarCheckExtraInfo, makePrettierLabel)

            case MatchEventEditorEventType.video_assistant_referee_over:
                return createOptions(MatchEventEditorVarOverExtraInfo, makePrettierLabel)

            default:
                return []
        }
    }, [selectedEventType])

    const cardLocation = useMemo<Option[]>(() => createOptions(MatchEventEditorCardLocation, makePrettierLabel), [])

    const cardReason = useMemo<Option[]>(() => createOptions(MatchEventEditorCardReason, makePrettierLabel), [])

    const penaltyMissedReason = useMemo<Option[]>(
        () => createOptions(MatchEventEditorPenaltyMissedReason, makePrettierLabel),
        [],
    )

    const substitutionReason = useMemo<Option[]>(
        () => createOptions(MatchEventEditorSubstitutionReason, makePrettierLabel),
        [],
    )

    const varOverReview = useMemo<Option[]>(
        () => createOptions(MatchEventEditorVarOverExtraInfo, makePrettierLabel),
        [],
    )

    const matchEvents = useMemo<Option[]>(() => createOptions(MatchEventEditorFixtureExtraInfo, makePrettierLabel), [])

    const types = useMemo<Option[]>(() => createOptions(MatchEventEditorEventType, getMatchEditorEventTypeLabel), [])

    const periods = useMemo<Option[]>(() => createOptions(MatchEventPeriodNumber, makePrettierLabel), [])

    const options = useMemo(
        () => ({
            teams,
            players,
            types,
            periods,
            extraInfo,
            cardLocation,
            cardReason,
            penaltyMissedReason,
            substitutionReason,
            varOverReview,
            matchEvents,
        }),
        [
            extraInfo,
            cardLocation,
            cardReason,
            penaltyMissedReason,
            substitutionReason,
            periods,
            players,
            teams,
            types,
            varOverReview,
            matchEvents,
        ],
    )

    return options
}
